/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupport.com)
 *
 * @description
 *
 * @created at Fri Sep 13 2024
 **/

import { useEffect, useRef } from "react";

import { _T } from "@rfice/language";
import { Column, Radio, Typography } from "@/frameworks/designStuff/components";
import { SidebarID } from "@/frameworks/core/commons";
import { useCreateWorkGroup, useSidebarState } from "@/frameworks/core/hooks";
import * as S from "../emotion";
import { marginBottomStyle } from "../emotion";

const ALLOW_TYPE = {
	publicRadio: "PUBLIC",
	privateRadio: "PRIVATE",
	enterPrivateRadio: "ENTIRE_PRIVATE"
};

export default function SearchAllowTypeField() {
	const radioRef = useRef(null);
	const {
		accessAllowType,
		handlers: { handleReset, handleAccessAllowType }
	} = useCreateWorkGroup();

	const sidebarState = useSidebarState();

	useEffect(() => {
		if (sidebarState.origin[SidebarID.CreateWorkGroup]?.isOpen === false) {
			handleReset(radioRef, () => handleAccessAllowType("PUBLIC"));
		}
	}, [sidebarState.origin[SidebarID.CreateWorkGroup]?.isOpen]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		const id = target.id as keyof typeof ALLOW_TYPE;
		handleAccessAllowType(ALLOW_TYPE[id] as WebServices.Group.Create.Request["accessAllowType"]);
	};

	return (
		<S.FieldBox css={marginBottomStyle(6)}>
			<S.Name typoSize="Body4">{_T("공개 설정")}</S.Name>
			<Column>
				<Radio
					id="publicRadio"
					data-type="PUBLIC"
					name="space-visibility-settings"
					labelPosition="right"
					checked={accessAllowType === "PUBLIC"}
					onChange={handleChange}
				>
					<Typography.p typoSize="Body3">{_T("전체 공개 (즉시 가입)")}</Typography.p>
				</Radio>
				<Radio
					id="privateRadio"
					data-type="PRIVATE"
					name="space-visibility-settings"
					labelPosition="right"
					onChange={handleChange}
					checked={accessAllowType === "PRIVATE"}
				>
					<Typography.p typoSize="Body3">{_T("비공개 (승인 후 가입)")}</Typography.p>
				</Radio>
				<Radio
					id="enterPrivateRadio"
					data-type="ENTIRE_PRIVATE"
					name="space-visibility-settings"
					labelPosition="right"
					onChange={handleChange}
					checked={accessAllowType === "ENTIRE_PRIVATE"}
				>
					<Typography.p typoSize="Body3">{_T("전체 비공개 (초대로만 가입)")}</Typography.p>
				</Radio>
			</Column>
		</S.FieldBox>
	);
}
